<template>
  <div>
    <div class="header-main">
      <div class="hm-left">
        <div>
          <div class="bread-crumb"></div>
          <div class="header-main__title-wrap">
            <h1 class="header-main__title">Настройки</h1>
            <div class="header-main__review"></div>
          </div>
        </div>
      </div>
      <div class="hm-right">
        <router-link to="/status-matrix" class="status-matrix-link">Матрица переходов статусов</router-link>
        <router-link to="/reviews-response" class="status-matrix-link">Ответы на отзывы</router-link>
      </div>
    </div>

    <div class="settings-section">
      <!-- Секция отправки сообщений -->
      <div class="message-section">
        <h3 class="message-section-title">Отправка сообщений</h3>
        <div class="message-form">
          <div class="form-row">
            <label class="form-label">Получатель:</label>
            <select v-model="messageRecipient" class="settings-select">
              <option value="all">Всем пользователям</option>
              <option v-for="user in users.filter(u => u.active)" :key="user.id" :value="user.id">
                {{ getUserFullName(user) }} ({{ user.label }})
              </option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Сообщение:</label>
            <textarea 
              v-model="messageText" 
              placeholder="Введите сообщение..." 
              class="message-textarea"
              rows="3"
            ></textarea>
          </div>
          <div class="form-row">
            <button @click="sendMessage" class="send-message-btn" :disabled="!messageText.trim()">
              Отправить сообщение
            </button>
          </div>
        </div>
      </div>

      <div class="settings-header">
        <label>
          <span class="settings-label">Группа для отображения:</span>
          <select v-model="selectedGroup" class="settings-select">
            <option value="">Все</option>
            <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
          </select>
        </label>
        
        <button @click="openAddUserModal" class="add-user-btn">
          <span class="btn-icon">+</span>
          Добавить пользователя
        </button>
      </div>

      <div class="table-wrapper">
        <div class="settings-table">
          <div class="settings-table-head">
            <div>Фото</div>
            <div>Имя и Фамилия</div>
            <div>Внутренний</div>
            <div>Мобильный</div>
            <div>Символьный код</div>
            <div>Активный</div>
            <div class="rating-col-head" title="Не показывать на странице «Рейтинг» и в статистике звонков">Скрыть в рейтинге</div>
            <div class="force-offline-col-head" title="Не участвует в раздаче заказов, даже если статус «Онлайн»">Откл. онлайн</div>
            <div>Группа</div>
            <div>Действия</div>
          </div>
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            class="settings-row"
          >
          <div class="photo-cell">
            <img v-if="user.photo" :src="user.photo" alt="Фото" class="user-photo" />
            <div v-else class="no-photo">📷</div>
          </div>
          <div class="user-name">
            {{ getUserFullName(user) }}
          </div>
          <div class="phone-cell">
            {{ user.internalPhone || '—' }}
          </div>
          <div class="phone-cell">
            {{ user.mobilePhone || '—' }}
          </div>
          <div class="phone-cell">
            {{ user.symbolicCode || '—' }}
          </div>
          <div class="active-cell">
            <label class="switch">
              <input type="checkbox" v-model="user.active" @change="saveUserChanges(user)">
              <span class="slider"></span>
            </label>
          </div>
          <div class="rating-hide-cell" :title="!user.active ? 'Неактивные в рейтинге не отображаются' : ''">
            <input
              type="checkbox"
              :checked="truthy(user.hideInStats)"
              :disabled="!user.active"
              @change="onHideInRatingChange(user, $event)"
            />
          </div>
          <div class="force-offline-cell" :title="forceOfflineTitle(user)">
            <input
              type="checkbox"
              :checked="truthy(user.forceOffline)"
              :disabled="!user.active || user.group_name !== 'Менеджеры'"
              @change="onForceOfflineChange(user, $event)"
            />
          </div>
          <div>
            <select v-model="user.group_name" class="settings-select" @change="saveUserChanges(user)">
              <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
            </select>
          </div>
          <div class="action-buttons">
            <button @click="openEditUserModal(user)" class="edit-btn" title="Редактировать">✏️</button>
            <button @click="deleteUser(user.id)" class="delete-btn" title="Удалить">🗑️</button>
          </div>
        </div>
      </div>
      </div>
    </div>
    
    <!-- Модальное окно добавления пользователя -->
    <div v-if="showAddUserModal" class="modal-overlay" @click="closeAddUserModal">
      <div class="modal-content large-modal" @click.stop>
        <h3>Добавить нового пользователя</h3>
        <form @submit.prevent="addUser" class="user-form">
          <div class="form-grid">
            <div class="form-row">
              <label class="form-label">Имя пользователя: *</label>
              <input v-model="newUser.label" required placeholder="Имя пользователя" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Имя:</label>
              <input v-model="newUser.firstName" placeholder="Имя" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Фамилия:</label>
              <input v-model="newUser.lastName" placeholder="Фамилия" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Пароль:</label>
              <input v-model="newUser.password" type="password" placeholder="Пароль" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Внутренний номер:</label>
              <input v-model="newUser.internalPhone" v-mask="'####'" placeholder="1234" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Мобильный номер:</label>
              <input v-model="newUser.mobilePhone" v-mask="'+375 (##) ###-##-##'" placeholder="+375 (29) 123-45-67" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">ID в ретейл CRM:</label>
              <input v-model="newUser.retailCrmId" placeholder="ID в ретейл CRM" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Символьный код:</label>
              <input v-model="newUser.symbolicCode" placeholder="Символьный код" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Логин в TalkMe:</label>
              <input v-model="newUser.talkMeLogin" placeholder="Логин в TalkMe" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">ID в TalkMe:</label>
              <input v-model="newUser.id_talkme" placeholder="ID в TalkMe" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Email:</label>
              <input v-model="newUser.email" type="email" placeholder="example@mail.com" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Группа:</label>
              <select v-model="newUser.group_name" class="settings-select">
                <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
              </select>
            </div>
            
            <div class="form-row">
              <label class="form-label">Активный пользователь:</label>
              <label class="switch">
                <input type="checkbox" v-model="newUser.active">
                <span class="slider"></span>
              </label>
            </div>

            <div class="form-row">
              <label class="form-label">Скрыть из рейтинга (для активных):</label>
              <input
                type="checkbox"
                :checked="truthy(newUser.hideInStats)"
                :disabled="!newUser.active"
                @change="e => { newUser.hideInStats = e.target.checked ? 1 : 0 }"
              />
            </div>

            <div class="form-row">
              <label class="form-label">Принудительно отключить онлайн (раздача заказов):</label>
              <input
                type="checkbox"
                :checked="truthy(newUser.forceOffline)"
                :disabled="!newUser.active || newUser.group_name !== 'Менеджеры'"
                @change="e => { newUser.forceOffline = e.target.checked ? 1 : 0 }"
              />
            </div>
            
            <div class="form-row">
              <label class="form-label">Задержка уведомления (сек.):</label>
              <input v-model.number="newUser.delay" type="number" min="0" placeholder="Задержка в секундах" class="settings-input" />
            </div>
            
            <div class="form-row full-width">
              <label class="form-label">Фото:</label>
              <input type="file" @change="handleFileUpload" accept="image/*" class="file-input" />
              <div v-if="newUser.photoPreview" class="photo-preview">
                <img :src="newUser.photoPreview" alt="Предпросмотр" class="preview-img" />
              </div>
            </div>
          </div>
          
          <div class="modal-buttons">
            <button type="submit" class="save-btn">Добавить пользователя</button>
            <button type="button" @click="closeAddUserModal" class="cancel-btn">Отмена</button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Модальное окно редактирования пользователя -->
    <div v-if="showEditUserModal" class="modal-overlay" @click="closeEditUserModal">
      <div class="modal-content large-modal" @click.stop>
        <h3>Редактировать пользователя: {{ editingUser.label }}</h3>
        <form @submit.prevent="updateUser" class="user-form">
          <div class="form-grid">
            <div class="form-row">
              <label class="form-label">Имя пользователя: *</label>
              <input v-model="editingUser.label" required placeholder="Имя пользователя" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Имя:</label>
              <input v-model="editingUser.firstName" placeholder="Имя" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Фамилия:</label>
              <input v-model="editingUser.lastName" placeholder="Фамилия" class="settings-input" />
            </div>
            
                         <div class="form-row">
               <label class="form-label">Внутренний номер:</label>
               <input v-model="editingUser.internalPhone" v-mask="'####'" placeholder="1234" class="settings-input" />
             </div>
             
             <div class="form-row">
               <label class="form-label">Мобильный номер:</label>
               <input v-model="editingUser.mobilePhone" v-mask="'+375 (##) ###-##-##'" placeholder="+375 (29) 123-45-67" class="settings-input" />
             </div>
            
            <div class="form-row">
              <label class="form-label">ID в ретейл CRM:</label>
              <input v-model="editingUser.retailCrmId" placeholder="ID в ретейл CRM" class="settings-input" />
            </div>
            
            <div class="form-row">
              <label class="form-label">Символьный код:</label>
              <input v-model="editingUser.symbolicCode" placeholder="Символьный код" class="settings-input" />
            </div>
            
                         <div class="form-row">
               <label class="form-label">Логин в TalkMe:</label>
               <input v-model="editingUser.talkMeLogin" placeholder="Логин в TalkMe" class="settings-input" />
             </div>
             
             <div class="form-row">
               <label class="form-label">ID в TalkMe:</label>
               <input v-model="editingUser.id_talkme" placeholder="ID в TalkMe" class="settings-input" />
             </div>
             
             <div class="form-row">
               <label class="form-label">Email:</label>
               <input v-model="editingUser.email" type="email" placeholder="example@mail.com" class="settings-input" />
             </div>
             
             <div class="form-row">
               <label class="form-label">Группа:</label>
               <select v-model="editingUser.group_name" class="settings-select">
                 <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
               </select>
             </div>

            <div class="form-row">
               <label class="form-label">Задержка уведомления (сек.):</label>
               <input v-model.number="editingUser.delay" type="number" min="0" placeholder="Задержка в секундах" class="settings-input" />
             </div>
             
             <div class="form-row">
               <label class="form-label">Активный пользователь:</label>
               <label class="switch">
                 <input type="checkbox" v-model="editingUser.active">
                 <span class="slider"></span>
               </label>
             </div>

            <div class="form-row">
              <label class="form-label">Скрыть из рейтинга / статистики звонков:</label>
              <input
                type="checkbox"
                :checked="truthy(editingUser.hideInStats)"
                :disabled="!editingUser.active"
                @change="e => { editingUser.hideInStats = e.target.checked ? 1 : 0 }"
              />
            </div>

            <div class="form-row">
              <label class="form-label">Принудительно отключить онлайн (раздача заказов):</label>
              <input
                type="checkbox"
                :checked="truthy(editingUser.forceOffline)"
                :disabled="!editingUser.active || editingUser.group_name !== 'Менеджеры'"
                @change="e => { editingUser.forceOffline = e.target.checked ? 1 : 0 }"
              />
            </div>

            <div class="form-row full-width">
              <label class="form-label">Фото:</label>
              <input type="file" @change="handleEditPhotoUpload" accept="image/*" class="file-input" />
              <div v-if="editingUser.photo" class="photo-preview">
                <img :src="editingUser.photo" alt="Текущее фото" class="preview-img" />
              </div>
            </div>
          </div>
          
          <div class="modal-buttons">
            <button type="submit" class="save-btn">Сохранить изменения</button>
            <button type="button" @click="changePassword(editingUser)" class="change-password-btn">Изменить пароль</button>
            <button type="button" @click="sendPasswordToEmail(editingUser)" class="send-password-btn" :disabled="!editingUser.email">Отправить пароль</button>
            <button type="button" @click="closeEditUserModal" class="cancel-btn">Отмена</button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Модальное окно для смены пароля -->
    <div v-if="showPasswordModal" class="modal-overlay" @click="closePasswordModal">
      <div class="modal-content" @click.stop>
        <h3>Изменить пароль для {{ selectedUser && selectedUser.label }}</h3>
        <input v-model="newPassword" type="password" placeholder="Новый пароль" class="settings-input" />
        <input v-model="confirmPassword" type="password" placeholder="Подтвердите пароль" class="settings-input" />
        <div class="modal-buttons">
          <button @click="updatePassword" class="save-btn">Сохранить</button>
          <button @click="closePasswordModal" class="cancel-btn">Отмена</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { VueMaskDirective } from 'v-mask'

// Регистрируем директиву маски
const vMask = VueMaskDirective
const groups = ['Руководство', 'Логисты', 'Контроль', 'Менеджеры', 'Брак', 'Склад', 'Магазины']
const users = ref([])
const selectedGroup = ref('')
const showPasswordModal = ref(false)
const showAddUserModal = ref(false)
const showEditUserModal = ref(false)
const selectedUser = ref(null)
const editingUser = ref({})
const newPassword = ref('')
const confirmPassword = ref('')
const messageRecipient = ref('all')
const messageText = ref('')

// Получаем WebSocket через inject на верхнем уровне
const wsProvider = inject('ws', null)

const newUser = ref({
  label: '',
  firstName: '',
  lastName: '',
  password: '',
  internalPhone: '',
  mobilePhone: '',
  retailCrmId: '',
  symbolicCode: '',
  talkMeLogin: '',
  group_name: groups[0],
  delay: 1,
  photo: null,
  photoPreview: null,
  active: true,
  hideInStats: false,
  forceOffline: false,
  id_talkme: '',
  email: ''
})

function getUserFullName(user) {
  const parts = []
  if (user.firstName) parts.push(user.firstName)
  if (user.lastName) parts.push(user.lastName)
  if (parts.length === 0) return user.label
  return parts.join(' ')
}

function truthy(v) {
  return v === 1 || v === true || v === '1'
}

function onHideInRatingChange(user, event) {
  if (!user.active) return
  user.hideInStats = event.target.checked ? 1 : 0
  saveUserChanges(user)
}

function onForceOfflineChange(user, event) {
  if (!user.active || user.group_name !== 'Менеджеры') return
  user.forceOffline = event.target.checked ? 1 : 0
  saveUserChanges(user)
}

function forceOfflineTitle(user) {
  if (!user.active) return 'Только для активных пользователей'
  if (user.group_name !== 'Менеджеры') return 'Только для группы «Менеджеры»'
  return 'Менеджер не получает заказы в автораздаче, даже при статусе «Онлайн»'
}

function openAddUserModal() {
  resetNewUserForm()
  showAddUserModal.value = true
}

function closeAddUserModal() {
  showAddUserModal.value = false
  resetNewUserForm()
}

function openEditUserModal(user) {
  editingUser.value = { ...user }
  showEditUserModal.value = true
}

function closeEditUserModal() {
  showEditUserModal.value = false
  editingUser.value = {}
}

function resetNewUserForm() {
  newUser.value = {
    label: '',
    firstName: '',
    lastName: '',
    password: '',
    internalPhone: '',
    mobilePhone: '',
    retailCrmId: '',
    symbolicCode: '',
    talkMeLogin: '',
    group_name: groups[0],
    delay: 1,
    photo: null,
    photoPreview: null,
    active: true,
    hideInStats: false,
    forceOffline: false,
    id_talkme: '',
    email: ''
  }
}

async function loadUsers() {
  const res = await fetch('https://manager.1akb.by/users-settings')
  users.value = await res.json()
}

onMounted(async () => {
  await loadUsers()
})

const filteredUsers = computed(() =>
  selectedGroup.value
    ? users.value.filter(u => u.group_name === selectedGroup.value)
    : users.value
)

async function saveUserChanges(user) {
  try {
    await fetch(`https://manager.1akb.by/users-settings/${user.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user)
    })
  } catch (error) {
    alert('Ошибка при сохранении изменений')
  }
}

function handleFileUpload(event) {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      newUser.value.photoPreview = e.target.result
      newUser.value.photo = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

function handleEditPhotoUpload(event) {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      editingUser.value.photo = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

function changePassword(user) {
  selectedUser.value = user
  newPassword.value = ''
  confirmPassword.value = ''
  showPasswordModal.value = true
}

function closePasswordModal() {
  showPasswordModal.value = false
  selectedUser.value = null
  newPassword.value = ''
  confirmPassword.value = ''
}

async function updatePassword() {
  if (newPassword.value !== confirmPassword.value) {
    alert('Пароли не совпадают!')
    return
  }
  
  if (newPassword.value.length < 6) {
    alert('Пароль должен содержать минимум 6 символов!')
    return
  }
  
  try {
    const res = await fetch(`https://manager.1akb.by/users-settings/${selectedUser.value.id}/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: newPassword.value })
    })
    
    if (res.ok) {
      alert('Пароль успешно изменен!')
      closePasswordModal()
    } else {
      alert('Ошибка при изменении пароля')
    }
  } catch (error) {
    alert('Ошибка соединения')
  }
}

async function addUser() {
  if (!newUser.value.label.trim()) return
  
  try {
    const res = await fetch('https://manager.1akb.by/users-settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser.value),
    })
    
    if (res.ok) {
      await loadUsers()
      closeAddUserModal()
      alert('Пользователь успешно добавлен!')
    } else {
      alert('Ошибка при добавлении пользователя')
    }
  } catch (error) {
    alert('Ошибка соединения')
  }
}

async function updateUser() {
  try {
    const res = await fetch(`https://manager.1akb.by/users-settings/${editingUser.value.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingUser.value)
    })
    
    if (res.ok) {
      await loadUsers()
      closeEditUserModal()
      alert('Пользователь успешно обновлен!')
    } else {
      alert('Ошибка при обновлении пользователя')
    }
  } catch (error) {
    alert('Ошибка соединения')
  }
}

async function deleteUser(id) {
  if (!confirm('Удалить пользователя?')) return
  
  try {
    const res = await fetch(`https://manager.1akb.by/users-settings/${id}`, {
      method: 'DELETE',
    })
    
    if (res.ok) {
      users.value = users.value.filter(u => u.id !== id)
      alert('Пользователь удален')
    } else {
      alert('Ошибка при удалении')
    }
  } catch (error) {
    alert('Ошибка соединения')
  }
}

async function sendPasswordToEmail(user) {
  if (!user.email) {
    alert('У пользователя не указан email!')
    return
  }
  
  if (!user.password) {
    alert('У пользователя не установлен пароль!')
    return
  }
  
  if (!confirm(`Отправить пароль на ${user.email}?`)) return
  
  try {
    const res = await fetch(`https://manager.1akb.by/users-settings/${user.id}/send-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    
    if (res.ok) {
      alert('Пароль успешно отправлен на email!')
    } else {
      const errorData = await res.json()
      alert(`Ошибка при отправке: ${errorData.error}`)
    }
  } catch (error) {
    alert('Ошибка соединения')
  }
}

function sendMessage() {
  if (!messageText.value.trim()) {
    alert('Введите сообщение!')
    return
  }

  // Получаем текущего пользователя из localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  
  // Получаем WebSocket через предоставленный провайдер
  console.log('wsProvider:', wsProvider)
  
  let ws = null
  if (wsProvider && typeof wsProvider === 'function') {
    ws = wsProvider()
    console.log('WebSocket через inject (функция):', ws)
  } else if (wsProvider && wsProvider.readyState !== undefined) {
    // Если это уже WebSocket объект
    ws = wsProvider
    console.log('WebSocket через inject (объект):', ws)
  }
  
  console.log('Итоговый WebSocket:', ws)
  console.log('WebSocket readyState:', ws ? ws.readyState : 'ws is null')
  console.log('WebSocket OPEN:', WebSocket.OPEN)
  console.log('Сравнение:', ws ? (ws.readyState === WebSocket.OPEN) : false)
  
  if (!ws) {
    console.error('WebSocket не найден')
    alert('WebSocket соединение не найдено. Пожалуйста, обновите страницу.')
    return
  }
  
  if (ws.readyState !== WebSocket.OPEN) {
    console.error('WebSocket не открыт:', {
      readyState: ws.readyState,
      expected: WebSocket.OPEN,
      CONNECTING: WebSocket.CONNECTING,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    })
    alert('WebSocket соединение не установлено. Пожалуйста, обновите страницу.')
    return
  }

  // Определяем получателя
  const recipientUserId = messageRecipient.value === 'all' ? null : parseInt(messageRecipient.value)
  const recipientUser = messageRecipient.value !== 'all' 
    ? users.value.find(u => u.id === recipientUserId)
    : null

  // Отправляем сообщение через WebSocket
  const messageData = {
    type: 'adminMessage',
    data: {
      message: messageText.value.trim(),
      recipientUserId: recipientUserId,
      recipientLogin: recipientUser ? recipientUser.talkMeLogin : null,
      senderUserId: currentUser.id,
      senderName: currentUser.label || 'Администратор',
      timestamp: new Date().toISOString()
    }
  }

  console.log('Отправляем сообщение:', messageData)
  
  try {
    ws.send(JSON.stringify(messageData))
    console.log('Сообщение успешно отправлено!')
    alert('Сообщение отправлено!')
    messageText.value = ''
  } catch (error) {
    console.error('Ошибка отправки сообщения:', error)
    alert('Ошибка при отправке сообщения: ' + error.message)
  }
}
</script>

<style scoped>
.settings-section {
  margin-top: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 8px #0001;
  padding: 24px;
  max-width: 100%;
  position: relative;
  overflow: visible;
}

.settings-header {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.add-user-btn {
  background: linear-gradient(180deg, #22c993, #20a77f) !important;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: bold;
  cursor: pointer;
  font-size: 15px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-user-btn:hover {
  background: #2e7031;
}

.btn-icon {
  font-size: 18px;
  font-weight: bold;
}

.settings-label {
  font-weight: bold;
  margin-right: 10px;
  font-size: 13px;
}

.settings-select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #d3d3d3;
  background: #fafafa;
  font-size: 13px;
  min-width: 120px;
  min-height: 40px;
  box-sizing: border-box;
}

.settings-input {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #d3d3d3;
  background: #fafafa;
  font-size: 13px;
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
}

.table-wrapper {
  position: relative;
  overflow: visible;
  width: 100%;
}

.settings-table {
  margin-bottom: 18px;
  overflow-x: auto;
  position: relative;
  width: 100%;
}

.settings-table-head,
.settings-row {
  display: grid;
  grid-template-columns: 80px 1fr 100px 130px 90px 90px 100px 88px minmax(100px, 1fr) 88px;
  gap: 12px;
  align-items: center;
}

.rating-col-head {
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
}

.rating-hide-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rating-hide-cell input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.rating-hide-cell input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.force-offline-col-head {
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
}

.force-offline-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.force-offline-cell input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.force-offline-cell input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.settings-table-head {
  font-weight: bold;
  background: #f3f6f8;
  padding: 15px 20px;
  font-size: 14px;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0;
  border-radius: 0;
}

/* Добавляем border-radius только когда не sticky */
.settings-table:not(.scrolled) .settings-table-head {
  border-radius: 6px 6px 0 0;
}

.settings-row {
  border-bottom: 1px solid #eaeaea;
  padding: 15px 20px;
  transition: background 0.2s;
}

.settings-row:last-child {
  border-bottom: none;
}

.settings-row:hover {
  background: #f9f9fb;
}

.photo-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-photo {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
}

.no-photo {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 50%;
  font-size: 20px;
  color: #999;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.phone-cell {
  font-size: 13px;
  color: #666;
  
}

.active-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

/* Стили для переключателя */
.settings-section .switch {
  position: relative !important;
  display: inline-block !important;
  width: 50px !important;
  height: 24px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  background: none !important;
  vertical-align: middle !important;
  flex-shrink: 0 !important;
}

.settings-section .switch input[type="checkbox"] {
  opacity: 0 !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  position: absolute !important;
  left: -9999px !important;
}

.settings-section .switch .slider {
  position: absolute !important;
  cursor: pointer !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: #ccc !important;
  transition: all 0.4s ease !important;
  border-radius: 24px !important;
  border: none !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  outline: none !important;
}

.settings-section .switch .slider:before {
  position: absolute !important;
  content: "" !important;
  height: 18px !important;
  width: 18px !important;
  left: 3px !important;
  bottom: 3px !important;
  background-color: white !important;
  transition: all 0.4s ease !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
  border: none !important;
}

.settings-section .switch input[type="checkbox"]:checked + .slider {
  background-color: #2979ff !important;
}

.settings-section .switch input[type="checkbox"]:checked + .slider:before {
  transform: translateX(26px) !important;
}

.settings-section .switch .slider:hover {
  box-shadow: 0 0 8px rgba(41, 121, 255, 0.3) !important;
}

.settings-section .switch input[type="checkbox"]:focus + .slider {
  outline: 2px solid #2979ff !important;
  outline-offset: 2px !important;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
}

.edit-btn, .delete-btn {
  background: transparent;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.15s;
}

.edit-btn:hover {
  background: #e3f2fd;
}

.delete-btn:hover {
  background: #faebeb;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  min-width: 400px;
  max-width: 70vw;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.large-modal {
  min-width: 700px;
  width: 80vw;
}

.modal-content h3 {
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
}

.user-form {
  display: flex;
  flex-direction: column;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
}

.form-row.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-weight: bold;
    font-size: 12px;
    color: #555;
    margin-bottom: 2px;
    line-height: 1.4;
    min-height: 16px;
    min-width: 100%;
}

.file-input {
  padding: 8px 12px;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  background: #fafafa;
  font-size: 13px;
  min-height: 40px;
  box-sizing: border-box;
  width: 100%;
}

.photo-preview {
  margin-top: 10px;
}

.preview-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.modal-buttons {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.save-btn {
  padding: 12px 24px;
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.18s;
  box-shadow: 0 1px 8px #0002;
}

.save-btn:hover {
  background: #1852a6;
}

.change-password-btn {
  padding: 12px 20px;
  background: #ff9800;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.18s;
}

.change-password-btn:hover {
  background: #f57c00;
}

.send-password-btn {
  padding: 12px 20px;
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.18s;
}

.send-password-btn:hover:not(:disabled) {
  background: #388e3c;
}

.send-password-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 12px 20px;
  background: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.settings-section {
  width: 96%;
}

.settings-header label {
  max-width: 100%;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.header-main {
  display: table;
  empty-cells: hide;
  padding: 32px 0 24px;
}

.filter-page .header-main {
  background-color: #fff;
  margin: 0 auto;
  padding-bottom: 21px;
  padding-left: 40px;
  padding-right: 40px;
  width: 100%;
}

.hm-left {
  color: #1e2248;
  float: left;
  width: 75%;
}

.header-main .hm-left, .header-main .hm-right {
  display: table-cell;
  float: none;
  vertical-align: top;
}

.header-main .hm-left {
  width: auto;
}


.hm-left h1 {
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -.3px;
  line-height: 32px;
  margin-top: 10px;
}

.header-main__title:not(:last-child) {
  margin-right: 16px;
}

.message-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f9f9fb;
  border-radius: 8px;
  border: 1px solid #eaeaea;
}

.message-section-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.message-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message-textarea {
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #d3d3d3;
  background: #fff;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;
}

.message-textarea:focus {
  outline: none;
  border-color: #2979ff;
}

.send-message-btn {
  padding: 12px 24px;
  background: linear-gradient(180deg, #22c993, #20a77f);
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.18s;
  box-shadow: 0 1px 8px #0002;
  align-self: flex-start;
}

.send-message-btn:hover:not(:disabled) {
  background: #2e7031;
}

.send-message-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.status-matrix-link {
  display: inline-block;
  padding: 10px 18px;
  background: #2979ff;
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  transition: background 0.2s;
}

.status-matrix-link:hover {
  background: #1852a6;
  color: #fff;
}
</style>
